import React, { useRef, useEffect, useState, useMemo } from "react";
import AdminPageLayout from "../../../components/FullPageLayout/AdminPageLayout";
import { Box, Tooltip, Typography, Button } from "@mui/material";
import { Add } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import {
  fetchCourses,
  addCourse
} from "../../../redux/actions/contentCoursesActions";
import ContentOwnerIcon from "../../../components/Icons/ContentOwnerIcon";
import CourseFlags from "../../../components/Icons/CourseFlags";
import CourseTypeIcon from "../../../components/Icons/CourseTypeIcon";
import TrainingHoursCell from "../../../components/Table/TrainingHoursCell";
import CoursesFilters from "./CoursesFilters";
import AddNewCourseModal from "./AddNewCourseModal";
import CourseInfoModal from "./CourseDetails/CourseInfoModal";
import { PropTypes } from "prop-types";
import Table from "../../../components/Table/Table";
import { push } from "connected-react-router";
import { useTranslation } from "react-i18next";
import makeStyles from "@mui/styles/makeStyles";
import { fetchTableData } from "../../../redux/utils/jsona";
import usePermissions from "../../../redux/selectors/usePermissions";
import isEqual from "lodash/isEqual";
import { buildCourseDetailsRoute, manage } from "../../../routes/AdminRoutes";
import useTableState from "../../../hooks/useTableState";
import { updateTableStatus } from "../../../redux/actions/tablesActions";
import { isIframed } from "../../../utils/iframe";

const useStyles = makeStyles(theme => ({
  tooltip: theme.mixins.tooltipLight
}));

const CourseNameCell = ({ name, flags, contentOwner, clientOwned }) => {
  return (
    <Box display="flex" alignItems="center">
      <Box mr={2}>
        <Typography color="textPrimary" variant="body2">
          {name}
        </Typography>
      </Box>
      {flags && <CourseFlags flags={flags} />}
      {contentOwner && (
        <ContentOwnerIcon
          contentOwner={contentOwner}
          clientOwned={clientOwned}
        />
      )}
    </Box>
  );
};

const CellWithTooltip = ({ hasAccess, children }) => {
  const { tooltip } = useStyles();
  const { t } = useTranslation();
  return !hasAccess ? (
    <Tooltip
      classes={{ tooltip }}
      placement="top"
      style={{ position: "relative", display: "flex" }}
      disablePortal
      title={
        <Typography color="textSecondary" variant="inherit" noWrap>
          {t("manageCourses.coursesTooltipMessage")}
        </Typography>
      }
    >
      <div>{children}</div>
    </Tooltip>
  ) : (
    children
  );
};

CourseNameCell.propTypes = {
  flags: PropTypes.arrayOf(PropTypes.string),
  name: PropTypes.string
};

const getCoursesTableColumns = ({ columns }) => [
  {
    title: "Type",
    field: "type",
    options: {
      sorting: true
    },
    cellStyle: {
      width: 40,
      textAlign: "center"
    },
    defaultSort: columns.type.orderDirection,
    render: rowData => <CourseTypeIcon type={rowData.type} />,
    customSort: () => 0
  },
  {
    title: "Name",
    field: "name",
    options: {
      sorting: true,
      filtering: true
    },
    cellStyle: {
      minWidth: 100
    },
    defaultFilter: columns.name.filterValue,
    defaultSort: columns.name.orderDirection,
    filterPlaceholder: "nameAll",
    render: rowData => (
      <CellWithTooltip hasAccess={rowData.hasAccess}>
        <CourseNameCell
          name={rowData.name}
          flags={rowData.flags}
          contentOwner={rowData.contentOwner}
          clientOwned={rowData.clientOwned}
        />
      </CellWithTooltip>
    )
  },
  {
    title: "Training Hours",
    field: "trainingHours",
    defaultSort: columns.trainingHours.orderDirection,
    options: {
      sorting: true
    },
    cellStyle: {
      minWidth: 130
    },
    render: rowData => <TrainingHoursCell duration={rowData.trainingHours} />
  },
  {
    title: "Assigned",
    field: "assigned",
    defaultSort: columns.assigned.orderDirection,
    options: {
      sorting: true
    },
    cellStyle: {
      minWidth: 100
    }
  },
  {
    title: "In Progress",
    field: "inProgress",
    defaultSort: columns.inProgress.orderDirection,
    options: {
      sorting: true
    },
    cellStyle: {
      minWidth: 100
    }
  },
  {
    defaultSort: columns.completed.orderDirection,
    options: {
      sorting: true
    },
    title: "Completed",
    field: "completed",
    cellStyle: {
      minWidth: 100
    }
  }
];

const Courses = () => {
  const dispatch = useDispatch();
  const tableRef = useRef(null);
  const { t } = useTranslation();
  const {
    canManageContent,
    canAddCourse,
    canManageInstructors,
    ...permissions
  } = usePermissions();
  const tableState = useTableState("courses");
  const [addNewCourseModalOpen, setAddNewCourseModalOpen] = useState(false);
  const [coursesFilters, setCoursesFilters] = useState(tableState.filters);
  const [infoModalCourseId, setInfoModalCourseId] = useState(null);
  const [isFetched, setIsFetched] = useState(false);

  useEffect(() => {
    updateTable();
  }, [coursesFilters]);

  useEffect(() => {
    setCoursesFilters(tableState.filters);
  }, []);

  const updateTable = () => {
    if (tableRef.current) {
      const query = { ...tableRef.current.state.query, page: 0 };
      tableRef.current.onQueryChange(query);
    }
  };

  const columns = useMemo(() => {
    return getCoursesTableColumns(tableState);
  }, []);

  const handleRowClick = rowData => {
    if (canManageContent) {
      dispatch(push(buildCourseDetailsRoute(rowData.id)));
    } else {
      setInfoModalCourseId(rowData.id);
    }
  };

  const handleAddNewCourse = async newCourseData => {
    const result = await dispatch(addCourse(newCourseData));
    handleRowClick(result);
  };

  const handleFetchCourses = tableQueryData => {
    if (!isFetched) {
      tableQueryData.page = tableState.page;
    }
    dispatch(updateTableStatus("courses", tableQueryData, coursesFilters));
    return fetchTableData(
      dispatch(
        fetchCourses(
          null,
          {
            type:
              permissions.isInstructor && !permissions.isAdmin ? "ilt" : null,
            ...coursesFilters,
            status: coursesFilters.status.value
          },
          tableQueryData
        )
      )
    ).then(response => {
      setIsFetched(true);
      return response;
    });
  };

  /**
   * Called when the filters that are applied to the courses results are updated
   */
  const handleApplyFilters = newFilters => {
    const filters = {
      ...coursesFilters,
      ...newFilters
    };

    if (!isEqual(coursesFilters, filters)) {
      setCoursesFilters(filters);
    }
  };

  const coursesTable = useMemo(() => {
    return (
      <Table
        itemType={t("manageCourses.coursesTableItemType")}
        columns={columns}
        data={handleFetchCourses}
        onRowClick={(e, rowData) => handleRowClick(rowData)}
        rowClickDisabled={rowData => !rowData.hasAccess}
        tableRef={tableRef}
        initialPage={tableState.page}
        defaultPerPage={tableState.pageSize}
      />
    );
  }, [coursesFilters, isFetched]);

  const expandFiltersByDefault =
    tableState.filters.hideAdditionalContent ||
    !!tableState.filters.status ||
    !!tableState.filters.contentPackage;

  return (
    <AdminPageLayout
      title={
        permissions.isInstructor && !permissions.isAdmin
          ? t("manageCourses.coursesPageInstructorTitle")
          : isIframed
          ? " "
          : t("manageCourses.coursesPageAdminTitle")
      }
      headerActions={
        canManageInstructors && (
          <Button
            variant="outlined"
            color="primary"
            onClick={() => dispatch(push(manage))}
          >
            {t("manageCourses.ManangeInstrAndLocButton")}
          </Button>
        )
      }
    >
      <Box my={4}>
        {canAddCourse && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => setAddNewCourseModalOpen(true)}
            data-qa="add-course-btn"
          >
            {t("manageCourses.addCourseButton")}
          </Button>
        )}
        <CoursesFilters
          onApplyFilters={handleApplyFilters}
          defaultValues={coursesFilters}
          expandedByDefault={expandFiltersByDefault}
        />
        {coursesTable}
      </Box>
      <AddNewCourseModal
        open={addNewCourseModalOpen}
        onClose={() => setAddNewCourseModalOpen(false)}
        onSubmitCourse={handleAddNewCourse}
      />
      <CourseInfoModal
        onClose={() => setInfoModalCourseId(null)}
        open={!!infoModalCourseId}
        courseId={infoModalCourseId}
      />
    </AdminPageLayout>
  );
};

Courses.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      /**
       * The course ID for the course
       */
      courseId: PropTypes.string
    })
  })
};

export default Courses;
